import { FC, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { useAppSelector } from '../../../../hooks';
import { selectAuth } from '../../../../features/authSlice';
import { constraintConfig } from '../../../../constraintConfig';

const Error404: FC = () => {
  const { roleID, isImpersonating } = useAppSelector(selectAuth);
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const impersonationInProgress = localStorage.getItem('impersonationInProgress') == 'true';
  useEffect(() => {
    // Check if impersonation is in progress
    if (impersonationInProgress) {
      // Auto redirect after countdown
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);

            // Determine redirect path based on role
            let redirectPath = '/dashboard';
            if (roleID === 10) {
              redirectPath = '/ap-approved';
            } else if (roleID === 13) {
              redirectPath = '/portal-support-requests';
            } else if (roleID === constraintConfig.roleID.roleExecutive) {
              redirectPath = '/approval-queue';
            }

            navigate(redirectPath, { replace: true });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [impersonationInProgress, roleID, navigate]);

  // If impersonation is in progress, show the waiting message
  if (impersonationInProgress) {
    return (
      <>
        <style>{`
          .spinner.spinner-lg {
            width: 80px;
            height: 80px;
            border-width: 4px;
          }
          
          .progress-bar {
            transition: width 1s linear;
          }
          
          .min-h-250px {
            min-height: 250px;
          }
        `}</style>

        <div className='container-fluid'>
          <div className="row align-items-center justify-content-center min-vh-100">
            <div className="col-md-8 col-lg-6">
              {/* Main Card */}
              <div className='card shadow-sm border-0'>
                <div className='card-body p-10 p-lg-15 text-center'>

                  {/* Spinner with Icon */}
                  <div className='mb-10 position-relative'>
                    <div className='spinner-border text-primary spinner-lg' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                    <div className='position-absolute top-50 start-50 translate-middle'>
                      <i className='ki-outline ki-arrow-cycle fs-2x text-primary'></i>
                    </div>
                  </div>

                  {/* Main Message */}
                  <h1 className='fw-bold text-gray-800 mb-4'>
                    Impersonation in Progress
                  </h1>

                  {/* User Type Badge */}
                  <div className='mb-7'>
                    <span className='badge badge-light-primary badge-lg px-6 py-3'>
                      <span className='fs-3 fw-semibold text-primary'>
                        Please wait while we set up your session
                      </span>
                    </span>
                  </div>

                  <p className='fs-3 text-gray-600 mb-2'>
                    You are being securely logged in!
                  </p>
                  {/* Progress Section */}
                  <div className='mb-10'>
                    <div className='d-flex justify-content-between mb-3'>
                      <span className='text-gray-600 fw-semibold'>Redirecting automatically</span>
                      <span className='badge badge-light-primary badge-lg px-4 py-2 fs-5'>
                        {countdown} second{countdown !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className='progress h-15px bg-light-primary'>
                      <div
                        className='progress-bar progress-bar-striped progress-bar-animated bg-primary'
                        role='progressbar'
                        style={{ width: `${((5 - countdown) / 5) * 100}%` }}
                        aria-valuenow={((5 - countdown) / 5) * 100}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                  </div>

                  {/* Info Cards */}
                  <div className='row g-5 mb-8'>
                    <div className='col-md-6'>
                      <div className='card card-dashed border-primary bg-light-primary'>
                        <div className='card-body py-5'>
                          <i className='ki-outline ki-security-user fs-2x text-primary mb-3'></i>
                          <h4 className='fw-bold text-gray-800 mb-1'>Secure Session</h4>
                          <p className='text-gray-600 mb-0 fs-6'>Your impersonation session is being established securely</p>
                        </div>
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='card card-dashed border-primary bg-light-primary'>
                        <div className='card-body py-5'>
                          <i className='ki-outline ki-arrow-rotate-left fs-2x text-primary mb-3'></i>
                          <h4 className='fw-bold text-gray-800 mb-1'>Auto Redirect</h4>
                          <p className='text-gray-600 mb-0 fs-6'>You'll be automatically redirected to your dashboard</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Manual Redirect Button */}
                  <div className='text-center'>
                    <p className='text-gray-500 mb-3'>Not redirecting automatically?</p>
                    <button
                      className='btn btn-primary'
                      onClick={() => {
                        let redirectPath = '/dashboard';
                        if (roleID === 10) {
                          redirectPath = '/ap-approved';
                        } else if (roleID === 13) {
                          redirectPath = '/portal-support-requests';
                        } else if (roleID === constraintConfig.roleID.roleExecutive) {
                          redirectPath = '/approval-queue';
                        }
                        navigate(redirectPath, { replace: true });
                      }}
                    >
                      <i className='ki-outline ki-arrow-right fs-2 me-2'></i>
                      Redirect Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Original 404 page
  return (
    <div className='container-fluid'>
      <div className="row align-items-center justify-content-center min-vh-100">
        <div className="col-md-4">
          <img
            src={toAbsoluteUrl('/media/illustrations/sketchy-1/404.png')}
            alt='404 illustration'
            className='w-100 mb-10 h-lg-550px'
          />
        </div>
        <div className="col-md-5">
          <h1 className='fs-2qx fw-bold mb-7'>
            Oops
            <br />
            <span className='text-primary'>nothing</span> here...
          </h1>
          <h4 className='fw-semibold mb-10 text-gray-500'>
            Uh oh, we can't seem to find the page you're looking for. <br />
            Try going back to home page or contact us for more information.
          </h4>

          {/* Return Home Button */}
          {roleID === constraintConfig.roleID.role10 ? (
            <Link to='/ap-approved' className='btn btn-primary'>
              <i className='ki-outline ki-home fs-2 me-2'></i>
              Return Home
            </Link>
          ) : roleID === constraintConfig.roleID.roleIDPortalSupport ? (
            <Link to='/portal-support-requests' className='btn btn-primary'>
              <i className='ki-outline ki-home fs-2 me-2'></i>
              Return Home
            </Link>
          ) : (
            <Link to='/' className='btn btn-primary'>
              <i className='ki-outline ki-home fs-2 me-2'></i>
              Return Home
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

export { Error404 }